<template>
    <div class="admin-question-entry-dashboard animation-fadeIn">
        <!-- Page Header -->
        <div class="page-header">
            <div class="header-info">
                <h2 class="page-title">题目录入中心</h2>
                <p class="page-subtitle">支持单题精细化录入与整卷模式批量录入，集成 Markdown 与 LaTeX 公式支持</p>
            </div>
            <div class="header-actions">
                <el-radio-group v-model="mode" size="large" class="mode-toggle-buttons">
                    <el-radio-button label="single">单题模式</el-radio-button>
                    <el-radio-button label="paper">整卷模式</el-radio-button>
                </el-radio-group>
            </div>
        </div>

        <div class="entry-layout">
            <el-row :gutter="24">
                <!-- Editor Column -->
                <el-col :lg="15" :md="24">
                    <div class="editor-column-wrapper">
                        <!-- Mode Info Alert -->
                        <el-alert v-if="mode === 'paper'" title="当前为整卷模式" type="info" show-icon :closable="false"
                            style="margin-bottom: 20px">
                            您录入的题目将暂存到右侧列表，最后统一提交为一个试卷实体。
                        </el-alert>

                        <!-- Basic Paper Info (Only for Paper mode) -->
                        <el-card v-if="mode === 'paper'" shadow="never" class="modern-card form-card"
                            style="margin-bottom: 24px">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title"><el-icon>
                                            <Notebook />
                                        </el-icon> 试卷名称设定</span>
                                </div>
                            </template>
                            <el-input v-model="paperTitle" placeholder="请输入试卷完整名称（如：2024上海高一期中数学）" />
                        </el-card>

                        <!-- Question Content Card -->
                        <el-card shadow="never" class="modern-card form-card">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title"><el-icon>
                                            <EditPen />
                                        </el-icon> 题目主要内容</span>
                                </div>
                            </template>

                            <el-form :model="currentQuestion" label-position="top" class="modern-form">
                                <el-form-item label="题干内容 (支持 Markdown / LaTeX)" required>
                                    <MdEditor v-model="currentQuestion.content" preview-theme="cyanosis"
                                        style="height: 280px;" @on-upload-img="onUploadImg" />
                                </el-form-item>

                                <el-row :gutter="16">
                                    <el-col :span="8">
                                        <el-form-item label="题型" required>
                                            <el-select v-model="currentQuestion.type" style="width: 100%">
                                                <el-option label="单选题" value="single_choice" />
                                                <el-option label="多选题" value="multiple_choice" />
                                                <el-option label="填空题" value="fill_in" />
                                                <el-option label="解答题" value="essay" />
                                                <el-option label="证明题" value="proof" />
                                                <el-option label="应用题" value="application" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                    <el-col :span="8">
                                        <el-form-item label="难度" required>
                                            <el-select v-model="currentQuestion.difficult" style="width: 100%">
                                                <el-option label="容易" value="容易" />
                                                <el-option label="较易" value="较易" />
                                                <el-option label="适中" value="适中" />
                                                <el-option label="较难" value="较难" />
                                                <el-option label="困难" value="困难" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                    <el-col :span="8">
                                        <el-form-item label="适用年级">
                                            <el-select v-model="currentQuestion.grade" style="width: 100%">
                                                <el-option label="高一" value="高一" />
                                                <el-option label="高二" value="高二" />
                                                <el-option label="高三" value="高三" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                </el-row>

                                <el-row :gutter="16">
                                    <el-col :span="6">
                                        <el-form-item label="年份">
                                            <el-input v-model="currentQuestion.year" placeholder="2024" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :span="9">
                                        <el-form-item label="地区">
                                            <el-cascader :options="pcTextArr" v-model="currentQuestion.area"
                                                style="width: 100%" clearable placeholder="选择省市" />
                                        </el-form-item>
                                    </el-col>
                                    <el-col :span="9">
                                        <el-form-item label="考试场景">
                                            <el-select v-model="currentQuestion.scene" placeholder="选择或输入"
                                                style="width: 100%" filterable allow-create>
                                                <el-option label="月考" value="月考" />
                                                <el-option label="期中" value="期中" />
                                                <el-option label="期末" value="期末" />
                                                <el-option label="模拟" value="模拟" />
                                                <el-option label="真题" value="真题" />
                                                <el-option label="同步" value="同步" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                </el-row>

                                <!-- Options for Choices -->
                                <div v-if="currentQuestion.type === 'single_choice' || currentQuestion.type === 'multiple_choice'"
                                    class="options-section">
                                    <div class="field-label-custom">选项设置</div>
                                    <div class="options-grid-modern">
                                        <div v-for="(option, index) in currentQuestion.options" :key="index"
                                            class="option-card-row">
                                            <div class="opt-id-circle">{{ String.fromCharCode(65 + index) }}</div>
                                            <el-input v-model="currentQuestion.options[index]"
                                                :placeholder="'输入选项 ' + String.fromCharCode(65 + index) + ' 的内容'" />
                                        </div>
                                    </div>
                                    <el-form-item label="标记正确答案" style="margin-top: 15px">
                                        <el-radio-group v-if="currentQuestion.type === 'single_choice'"
                                            v-model="currentQuestion.singleAnswer">
                                            <el-radio-button label="A">A</el-radio-button>
                                            <el-radio-button label="B">B</el-radio-button>
                                            <el-radio-button label="C">C</el-radio-button>
                                            <el-radio-button label="D">D</el-radio-button>
                                        </el-radio-group>
                                        <el-checkbox-group v-else v-model="currentQuestion.multiAnswers">
                                            <el-checkbox-button label="A">A</el-checkbox-button>
                                            <el-checkbox-button label="B">B</el-checkbox-button>
                                            <el-checkbox-button label="C">C</el-checkbox-button>
                                            <el-checkbox-button label="D">D</el-checkbox-button>
                                        </el-checkbox-group>
                                    </el-form-item>
                                </div>

                                <el-form-item label="答案内容">
                                    <MdEditor v-model="currentQuestion.answer" preview-theme="cyanosis"
                                        style="height: 180px;" />
                                </el-form-item>

                                <el-form-item label="详细解析">
                                    <MdEditor v-model="currentQuestion.explanation" preview-theme="cyanosis"
                                        style="height: 180px;" />
                                </el-form-item>
                            </el-form>
                        </el-card>

                        <!-- Meta: KPs and Tags -->
                        <el-card shadow="never" class="modern-card meta-card" style="margin-top: 24px">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title"><el-icon>
                                            <CollectionTag />
                                        </el-icon> 标签与知识点</span>
                                </div>
                            </template>

                            <div class="meta-forms">
                                <div class="meta-item">
                                    <div class="field-label-custom">关联知识点</div>
                                    <div class="tag-management-box">
                                        <div class="tags-display">
                                            <el-tag v-for="(kp, index) in currentQuestion.knowledgePoints" :key="index"
                                                closable @close="removeKnowledgePoint(index)" class="modern-tag-large">
                                                {{ kp }}
                                            </el-tag>
                                            <span v-if="currentQuestion.knowledgePoints.length === 0"
                                                class="empty-hint">未选择知识点</span>
                                        </div>
                                        <div class="tag-input-v2">
                                            <el-autocomplete v-model="newKnowledgePoint"
                                                :fetch-suggestions="filterKnowledgePoints" placeholder="搜索并选择知识点..."
                                                size="large" @select="handleSelectKnowledgePoint" style="flex: 1;">
                                                <template #prefix><el-icon>
                                                        <Search />
                                                    </el-icon></template>
                                            </el-autocomplete>
                                            <el-button type="primary" plain size="large"
                                                @click="addKnowledgePoint">添加</el-button>
                                        </div>
                                    </div>
                                </div>

                                <div class="meta-item" style="margin-top: 24px">
                                    <div class="field-label-custom">特色标签</div>
                                    <div class="tag-management-box">
                                        <div class="tags-display">
                                            <el-tag v-for="(tag, index) in currentQuestion.tags" :key="index" closable
                                                @close="removeTag(index)" type="success" class="modern-tag-large">
                                                {{ tag }}
                                            </el-tag>
                                            <span v-if="currentQuestion.tags.length === 0"
                                                class="empty-hint">未设置标签</span>
                                        </div>
                                        <div class="tag-input-v2">
                                            <el-autocomplete v-model="newTag" :fetch-suggestions="filterTags"
                                                placeholder="输入标签，如：典型题、压轴题" size="large" @select="handleSelectTag"
                                                style="flex: 1;">
                                                <template #prefix><el-icon>
                                                        <CollectionTag />
                                                    </el-icon></template>
                                            </el-autocomplete>
                                            <el-button type="success" plain size="large" @click="addTag">添加</el-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </el-card>

                        <div class="footer-action-bar">
                            <el-button size="large" @click="resetForm">重置全部</el-button>
                            <el-button v-if="mode === 'single'" type="primary" size="large"
                                @click="submitSingleQuestion" block class="main-submit">
                                <el-icon>
                                    <CircleCheck />
                                </el-icon> 立即提交入库
                            </el-button>
                            <el-button v-else type="primary" size="large" @click="addToPaper" block class="main-submit">
                                <el-icon>
                                    <Plus />
                                </el-icon> 添加到试卷暂存区
                            </el-button>
                        </div>
                    </div>
                </el-col>

                <!-- Preview & Queue Column -->
                <el-col :lg="9" :md="24">
                    <div class="side-panel-wrapper">
                        <!-- Preview Card -->
                        <el-card shadow="never" class="modern-card preview-card sticky-side">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title"><el-icon>
                                            <View />
                                        </el-icon> 实时效果预览</span>
                                </div>
                            </template>

                            <div class="preview-scroll-container">
                                <div v-if="!currentQuestion.content" class="preview-empty">
                                    <el-empty description="输入题干查看渲染效果" />
                                </div>
                                <div v-else class="preview-content-area">
                                    <div class="preview-meta-labels">
                                        <el-tag size="small">{{ getQuestionTypeText(currentQuestion.type) }}</el-tag>
                                        <el-tag size="small" type="warning">{{ currentQuestion.difficult }}</el-tag>
                                        <el-tag size="small" type="info">{{ currentQuestion.grade }}</el-tag>
                                    </div>
                                    <div class="q-content-preview">
                                        <MdEditor v-model="currentQuestion.content" preview-only
                                            preview-theme="cyanosis" />
                                    </div>
                                    <div v-if="currentQuestion.answer" class="q-answer-preview">
                                        <div class="p-title">【答案】</div>
                                        <MdEditor v-model="currentQuestion.answer" preview-only
                                            preview-theme="cyanosis" />
                                    </div>
                                </div>
                            </div>
                        </el-card>

                        <!-- Paper Queue (Only for Paper mode) -->
                        <el-card v-if="mode === 'paper'" shadow="never" class="modern-card paper-queue-card"
                            style="margin-top: 24px">
                            <template #header>
                                <div class="card-header">
                                    <span class="card-title"><el-icon>
                                            <List />
                                        </el-icon> 试卷暂存队列</span>
                                    <el-tag round type="primary">{{ paperQuestions.length }} 题</el-tag>
                                </div>
                            </template>

                            <div class="queue-list">
                                <el-empty v-if="paperQuestions.length === 0" :image-size="60" description="暂无暂存题目" />
                                <div v-for="(q, index) in paperQuestions" :key="index" class="queue-item">
                                    <div class="qi-header">
                                        <span class="qi-idx">#{{ index + 1 }}</span>
                                        <span class="qi-type">{{ getQuestionTypeText(q.type) }}</span>
                                        <el-button type="danger" link @click="paperQuestions.splice(index, 1)"><el-icon>
                                                <Delete />
                                            </el-icon></el-button>
                                    </div>
                                    <div class="qi-body">{{ q.content.replace(/[#*`\n]/g, '').substring(0, 40) }}...
                                    </div>
                                </div>
                            </div>

                            <div v-if="paperQuestions.length > 0" class="queue-actions">
                                <el-button size="small" @click="resetPaper">清空暂存</el-button>
                                <el-button type="success" @click="submitPaper">确认并提交整卷</el-button>
                            </div>
                        </el-card>
                    </div>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
    Document, Notebook, EditPen, CollectionTag,
    Search, View, Plus, Delete, CircleCheck, List
} from '@element-plus/icons-vue'
import { pcTextArr } from 'element-china-area-data'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import api from '@/utils/api'
import { v4 as uuidv4 } from 'uuid'

const mode = ref('single')
const paperTitle = ref('')

const currentQuestion = reactive({
    content: '',
    type: 'fill_in',
    difficult: '适中',
    year: new Date().getFullYear().toString(),
    area: [],
    grade: '高一',
    source: '',
    scene: '',
    knowledgePoints: [],
    knowledgePointIds: [],
    tags: [],
    tagIds: [],
    options: ['', '', '', ''],
    answer: '',
    explanation: '',
    singleAnswer: '',
    multiAnswers: []
})

const paperQuestions = ref([])
const newKnowledgePoint = ref('')
const newTag = ref('')
const allKnowledgePoints = ref([])
const allTags = ref([])

const fetchAllKnowledgePoints = async () => {
    try {
        const res = await api.get('/api/knowledgePoints')
        allKnowledgePoints.value = res.data.knowledgePoints || res.data || []
    } catch (err) {
        console.error('知识点加载失败', err)
    }
}

const fetchAllTags = async () => {
    try {
        const res = await api.get('/api/tags')
        allTags.value = res.data || []
    } catch (err) {
        console.error('标签加载失败', err)
    }
}

const filterKnowledgePoints = (queryString, cb) => {
    const results = queryString
        ? allKnowledgePoints.value.filter(kp => kp.name.toLowerCase().includes(queryString.toLowerCase()))
        : allKnowledgePoints.value
    cb(results.map(kp => ({ value: kp.name, id: kp.id })))
}

const filterTags = (queryString, cb) => {
    const results = queryString
        ? allTags.value.filter(t => t.name.toLowerCase().includes(queryString.toLowerCase()))
        : allTags.value
    cb(results.map(t => ({ value: t.name, id: t.id })))
}

const handleSelectKnowledgePoint = (item) => {
    if (!currentQuestion.knowledgePoints.includes(item.value)) {
        currentQuestion.knowledgePoints.push(item.value)
        currentQuestion.knowledgePointIds.push(item.id)
    }
    newKnowledgePoint.value = ''
}

const handleSelectTag = (item) => {
    if (!currentQuestion.tags.includes(item.value)) {
        currentQuestion.tags.push(item.value)
        currentQuestion.tagIds.push(item.id)
    }
    newTag.value = ''
}

const addKnowledgePoint = async () => {
    const name = newKnowledgePoint.value.trim()
    if (!name) return
    const existing = allKnowledgePoints.value.find(kp => kp.name === name)
    if (existing) {
        if (!currentQuestion.knowledgePoints.includes(name)) {
            currentQuestion.knowledgePoints.push(name)
            currentQuestion.knowledgePointIds.push(existing.id)
        }
    } else {
        try {
            const res = await api.post('/api/knowledgePoints', { name })
            const newKp = res.data.knowledgePoint
            allKnowledgePoints.value.push(newKp)
            currentQuestion.knowledgePoints.push(name)
            currentQuestion.knowledgePointIds.push(newKp.id)
        } catch (err) {
            console.error('知识点创建失败', err)
        }
    }
    newKnowledgePoint.value = ''
}

const addTag = async () => {
    const name = newTag.value.trim()
    if (!name) return
    const existing = allTags.value.find(t => t.name === name)
    if (existing) {
        if (!currentQuestion.tags.includes(name)) {
            currentQuestion.tags.push(name)
            currentQuestion.tagIds.push(existing.id)
        }
    } else {
        try {
            const res = await api.post('/api/tags', { name })
            const newT = res.data.tag
            allTags.value.push(newT)
            currentQuestion.tags.push(name)
            currentQuestion.tagIds.push(newT.id)
        } catch (err) {
            console.error('标签创建失败', err)
        }
    }
    newTag.value = ''
}

const removeKnowledgePoint = (index) => {
    currentQuestion.knowledgePoints.splice(index, 1)
    currentQuestion.knowledgePointIds.splice(index, 1)
}

const removeTag = (index) => {
    currentQuestion.tags.splice(index, 1)
    currentQuestion.tagIds.splice(index, 1)
}

const onUploadImg = async (files, callback) => {
    const res = await Promise.all(
        files.map((file) => {
            return new Promise((rev) => {
                const form = new FormData()
                form.append('file', file)
                form.append('type', 'question')
                form.append('filename', `entry_img_${uuidv4().substring(0, 8)}`)
                api.post('/api/upload/image', form, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }).then(res => rev(res))
            })
        })
    )
    callback(res.map((item) => item.data.url))
}

const getQuestionTypeText = (type) => {
    const map = {
        'single_choice': '单选题',
        'multiple_choice': '多选题',
        'fill_in': '填空题',
        'essay': '解答题',
        'proof': '证明题',
        'application': '应用题'
    }
    return map[type] || type
}

const getQuestionTypeId = (type) => {
    const map = { 'single_choice': 1, 'fill_in': 2, 'essay': 3, 'proof': 4, 'application': 5, 'multiple_choice': 6 }
    return map[type] || 1
}

const getDifficultId = (diff) => {
    const map = { '容易': 1, '较易': 2, '适中': 3, '较难': 4, '困难': 5 }
    return map[diff] || 3
}

const getGradeId = (grade) => {
    const map = { '高一': 10, '高二': 11, '高三': 12 }
    return map[grade] || 10
}

const resetForm = () => {
    currentQuestion.content = ''
    currentQuestion.knowledgePoints = []
    currentQuestion.knowledgePointIds = []
    currentQuestion.tags = []
    currentQuestion.tagIds = []
    currentQuestion.options = ['', '', '', '']
    currentQuestion.answer = ''
    currentQuestion.explanation = ''
    currentQuestion.singleAnswer = ''
    currentQuestion.multiAnswers = []
}

const submitSingleQuestion = async () => {
    if (!currentQuestion.content) {
        ElMessage.warning('请填写题干内容')
        return
    }
    try {
        const data = {
            content: currentQuestion.content,
            answer: currentQuestion.answer,
            explanation: currentQuestion.explanation,
            subjectId: 2,
            type: getQuestionTypeId(currentQuestion.type),
            difficult: getDifficultId(currentQuestion.difficult),
            grade: getGradeId(currentQuestion.grade),
            year: currentQuestion.year,
            source: currentQuestion.source,
            scene: currentQuestion.scene,
            region: currentQuestion.area[0] || '',
            city: currentQuestion.area[1] || '',
            tagIds: currentQuestion.tagIds,
            knowledgePointIds: currentQuestion.knowledgePointIds,
            options: currentQuestion.options.map((content, idx) => {
                const label = String.fromCharCode(65 + idx)
                let isCorrect = false
                if (currentQuestion.type === 'single_choice') isCorrect = currentQuestion.singleAnswer === label
                else if (currentQuestion.type === 'multiple_choice') isCorrect = currentQuestion.multiAnswers.includes(label)
                return { label, content, isCorrect }
            })
        }
        await api.post('/api/questions', data)
        ElMessage.success('成功入库 1 道题目')
        resetForm()
    } catch (error) {
        ElMessage.error('入库失败')
    }
}

const addToPaper = () => {
    if (!currentQuestion.content) {
        ElMessage.warning('请填写题干内容')
        return
    }
    paperQuestions.value.push(JSON.parse(JSON.stringify(currentQuestion)))
    ElMessage.success('已添加到试卷暂存队列')
    resetForm()
}

const submitPaper = async () => {
    if (!paperTitle.value) {
        ElMessage.warning('请输入试卷名称')
        return
    }
    if (paperQuestions.value.length === 0) return
    try {
        for (const q of paperQuestions.value) {
            const data = {
                content: q.content,
                answer: q.answer,
                explanation: q.explanation,
                subjectId: 2,
                type: getQuestionTypeId(q.type),
                difficult: getDifficultId(q.difficult),
                grade: getGradeId(q.grade),
                year: q.year,
                source: paperTitle.value,
                tagIds: q.tagIds,
                knowledgePointIds: q.knowledgePointIds,
                options: q.options.map((content, idx) => {
                    const label = String.fromCharCode(65 + idx)
                    let isCorrect = false
                    if (q.type === 'single_choice') isCorrect = q.singleAnswer === label
                    else if (q.type === 'multiple_choice') isCorrect = q.multiAnswers?.includes(label)
                    return { label, content, isCorrect }
                })
            }
            await api.post('/api/questions', data)
        }
        ElMessage.success(`整卷 "${paperTitle.value}" 已成功提交`)
        paperTitle.value = ''
        paperQuestions.value = []
    } catch (error) {
        ElMessage.error('提交过程中出现错误')
    }
}

const resetPaper = () => {
    ElMessageBox.confirm('确定清空所有暂存题目吗？', '提示', { type: 'warning' }).then(() => {
        paperQuestions.value = []
    })
}

onMounted(() => {
    fetchAllKnowledgePoints()
    fetchAllTags()
})
</script>

<style scoped>
.admin-question-entry-dashboard {
    background-color: transparent;
}

.page-header {
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #1a1a1a;
}

.page-subtitle {
    margin: 4px 0 0;
    color: #606266;
    font-size: 14px;
}

.modern-card {
    border: none;
    border-radius: 12px;
}

.card-title {
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
}

.modern-form :deep(.el-form-item__label) {
    font-weight: 600;
    padding-bottom: 4px;
}

.field-label-custom {
    font-weight: 600;
    color: #606266;
    margin-bottom: 12px;
    font-size: 14px;
}

.options-grid-modern {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #f8fafc;
    padding: 16px;
    border-radius: 10px;
}

.option-card-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.opt-id-circle {
    width: 32px;
    height: 32px;
    background: #409eff;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    flex-shrink: 0;
}

.tag-management-box {
    background: #f8fafc;
    border-radius: 10px;
    padding: 16px;
}

.tags-display {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
    min-height: 40px;
    align-items: center;
}

.empty-hint {
    color: #909399;
    font-size: 13px;
    font-style: italic;
}

.tag-input-v2 {
    display: flex;
    gap: 12px;
}

.modern-tag-large {
    padding: 8px 12px;
    height: auto;
    font-size: 14px;
    border-radius: 6px;
}

.footer-action-bar {
    margin-top: 32px;
    display: flex;
    gap: 16px;
}

.main-submit {
    flex: 1;
}

.sticky-side {
    position: sticky;
    top: 24px;
}

.preview-scroll-container {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    padding-right: 4px;
}

.preview-meta-labels {
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
}

.q-content-preview {
    margin-bottom: 24px;
}

.p-title {
    font-weight: bold;
    color: #409eff;
    margin-bottom: 8px;
}

.paper-queue-card {
    border-top: 4px solid #409eff;
}

.queue-list {
    max-height: 400px;
    overflow-y: auto;
}

.queue-item {
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.2s;
}

.queue-item:hover {
    background: #f8fafc;
}

.qi-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.qi-idx {
    font-weight: 800;
    color: #909399;
    font-size: 12px;
}

.qi-type {
    font-weight: 600;
    font-size: 12px;
    color: #409eff;
    flex: 1;
}

.qi-body {
    font-size: 12px;
    color: #606266;
}

.queue-actions {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
